import { browserTimezone, validTimezone, calendarNow } from '../../../../../platform/util/calendarTimezone';
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../../../platform/components/auth/auth-context';
import { addMonths, format, isSameDay } from "date-fns";
import { SIGNUP_USER } from '../../../../../platform/graphql/mutations';
import useLoadReduxStoreBookings from '../../../../../platform/redux/reducers/loadReduxStoreBooking';
import { useSelectedBusiness } from '../../../../../platform/redux/store';
import { BusinessQueryNames, GET_GOOGLE_CALENDAR_EVENTS } from '../../../../../platform/graphql/queries';
import useLoadReduxStoreData from '../../../../../platform/redux/reducers/loadReduxStoreData';
import { useAuthStatus } from '../../../../../platform/util/useAuthStatus';
import { setToken } from '../../../../../platform/util/authUtils';
import { useCalendarPreferences } from '../../../../../platform/util/calendarPreferences';
import { calendarParts, fromCalendarDate } from '../../../../../platform/util/calendarSystem';

import { useTheme } from "@mui/material/styles";

const CalendarContext = createContext();

export const CalendarProvider = ({ children, myBookingTypes, initialCalendar = 'gregory' }) => {
  const theme = useTheme();
  const isProd = process.env.NODE_ENV === 'production';
  const { isLoggedIn } = useAuthStatus();

  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );
  const userContext = useContext(UserContext);
  const apolloClient = useApolloClient();
  const providerUser = userContext.providerUser;
  const [signUpUser] = useMutation(SIGNUP_USER);
  const { businessUrl } = useParams();
  const location = useLocation();
  const isMySchedule = location.pathname.includes('myschedule');
  const myScheduleSearchEmail = useMemo(
    () => String(new URLSearchParams(location.search || "").get("email") || "").trim(),
    [location.search]
  );
  const pollMs = isMySchedule ? (isProd ? 15000 : 5000) : (isProd ? 30000 : 10000);

  const [businessRegistered, setBusinessRegistered] = useState(false);
  const selectedBusiness = useSelectedBusiness();
  const calendarPreferences = useCalendarPreferences(null, initialCalendar);
  // Profile is the single source for account display preferences.
  const displayTimezone = validTimezone(calendarPreferences.timezone) ? calendarPreferences.timezone : browserTimezone();
  const navigate = useNavigate();
  const businessStaffs = selectedBusiness?.businessStaffs;

  // State for merged bookings.
  const [allBookingDatas, setAllBookingDatas] = useState([]);
  const [bookingDatas, setBookingDatas] = useState([]);
  const [holidayDatas, setHolidayDatas] = useState([]);
  const [googleEventsCache, setGoogleEventsCache] = useState([]);
  const [googleEventsEnabled, setGoogleEventsEnabled] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => calendarNow(displayTimezone));
  const [selectedDate, setSelectedDate] = useState(() => calendarNow(displayTimezone));

  const date = new Date(currentMonth);
  const fullYear = Number(date.getFullYear());

  const staffRecord = selectedBusiness?.businessStaffs?.find(
    (staff) => Number(staff.id) === providerUser.staffId
  );
  const loggedInStaffEmail = useMemo(() => {
    return String(
      staffRecord?.user?.email ||
      staffRecord?.email ||
      providerUser?.email ||
      ''
    ).trim().toLowerCase();
  }, [staffRecord?.user?.email, staffRecord?.email, providerUser?.email]);
  const currentMonthKey = useMemo(() => format(currentMonth, 'yyyy-MM-01'), [currentMonth]);

  useEffect(() => {
    if (typeof staffRecord?.showGoogleEvents === 'boolean') {
      setGoogleEventsEnabled(Boolean(staffRecord.showGoogleEvents));
    }
  }, [staffRecord?.showGoogleEvents]);

  // Load base bookings from store.
  const scheduleBusinessId = isMySchedule
    ? null
    : Number(selectedBusiness?.businessBasic?.id);
  const scheduleLookupEmail = isMySchedule
    ? (
        myScheduleSearchEmail ||
        providerUser?.email ||
        providerUser?.primaryEmail ||
        ""
      )
    : providerUser?.email;
  const { bookings: primaryBookings, loading: bookingsLoading, refetch: refetchBookings } =
    useLoadReduxStoreBookings(scheduleBusinessId, scheduleLookupEmail, fullYear, {
      fetchPolicy: 'network-only',
    });
  const ethiopianYear = calendarParts(format(currentMonth, 'yyyy-MM-dd'), 'ethiopic').year;
  const firstGregorianYear = Number(fromCalendarDate(ethiopianYear, 1, 1, 'ethiopic').slice(0, 4));
  const extraYear = calendarPreferences.displayCalendar === 'ethiopic' ? (fullYear === firstGregorianYear ? firstGregorianYear + 1 : firstGregorianYear) : null;
  const { bookings: extraBookings, refetch: refetchExtraBookings } = useLoadReduxStoreBookings(scheduleBusinessId, scheduleLookupEmail, extraYear, { publishToStore: false });
  const bookingsFromStore = useMemo(() => [...new Map([...(primaryBookings || []), ...(extraYear ? extraBookings || [] : [])].map(booking => [`${booking.id}:${booking.time}`, booking])).values()], [primaryBookings, extraBookings, extraYear]);

  const [googleEventsLoading, setGoogleEventsLoading] = useState(false);
  const [googleEventsError, setGoogleEventsError] = useState(null);

  const ensureBusinessIsLoaded = async () => {
    if (!providerUser?.defaultBusinessId) return;

    try {
      await reloadBusinesses();
      setBusinessRegistered(true);

      // const biz = selectedBusiness?.businessBasic;
      // if (!biz?.address || !biz?.cityId) {
      //   navigate('/biz/business');
      // }
    } catch (err) {
      console.error('Failed to load businesses:', err);
    }
  };

  // Register business if needed.
  useEffect(() => {
    if (!businessUrl && isMySchedule) return;

    if (!businessUrl && providerUser?.defaultBusinessId && !businessRegistered) {
      ensureBusinessIsLoaded();
    }
  }, [providerUser?.defaultBusinessId, businessRegistered]);

  const registerBusiness = () => {
    signUpUser({
      variables: { email: providerUser.email },
    })
      .then((result) => {
        if (result?.data?.login?.token) {
          setToken(result.data.login.token);
          userContext.login(result.data.login.token);
        }
      })
      .catch((error) => {
        console.error('Error registering business:', error);
      });
  };

  const refetchGoogleEvents = useCallback(async (override = {}) => {
    const monthValue =
      typeof override?.currentMonth === 'string'
        ? override.currentMonth
        : currentMonthKey;
    const monthValues = isMySchedule
      ? Array.from({ length: 12 }, (_, index) =>
          format(addMonths(new Date(monthValue), index), 'yyyy-MM-01')
        )
      : [monthValue];

    const providerStaffId = Number(providerUser?.staffId);
    const targetStaffIds = Number.isFinite(Number(override?.staffId))
      ? [Number(override.staffId)]
      : (Number.isFinite(providerStaffId) ? [providerStaffId] : []);

    if (!targetStaffIds.length) {
      setGoogleEventsCache([]);
      return { events: [] };
    }

    const staffById = new Map((businessStaffs || []).map((staff) => [Number(staff?.id), staff]));
    setGoogleEventsLoading(true);
    setGoogleEventsError(null);

    try {
      const results = await Promise.allSettled(
        targetStaffIds.flatMap((staffId) =>
          monthValues.map((currentMonthValue) =>
            apolloClient.query({
              query: GET_GOOGLE_CALENDAR_EVENTS,
              variables: { staffId, currentMonth: currentMonthValue },
              fetchPolicy: 'network-only',
            })
          )
        )
      );

      const uniqueEvents = new Map();
      const mergedEvents = [];
      for (let i = 0; i < results.length; i += 1) {
        const result = results[i];
        const requestedStaffId = Number(targetStaffIds[Math.floor(i / monthValues.length)]);
        if (result.status !== 'fulfilled') continue;
        const data = result.value?.data?.getGoogleCalendarEvents?.events;
        if (!Array.isArray(data)) continue;
        for (const event of data) {
          // Treat events from this fetch as owned by the queried staff feed.
          const ownerStaffId = requestedStaffId;
          const owner = staffById.get(ownerStaffId);
          const normalizedEvent = {
            ...event,
            staffId: ownerStaffId,
            staffName: event?.staffName || owner?.user?.username || owner?.name || '',
            staffEmail: String(
              event?.staffEmail ||
              event?.organizerEmail ||
              event?.creatorEmail ||
              event?.email ||
              owner?.user?.email ||
              owner?.email ||
              loggedInStaffEmail
            ).trim().toLowerCase(),
          };
          const dedupeKey = [
            normalizedEvent?.id || '',
            normalizedEvent?.time || '',
            normalizedEvent?.staffId || '',
            normalizedEvent?.htmlLink || '',
          ].join('|');
          if (uniqueEvents.has(dedupeKey)) continue;
          uniqueEvents.set(dedupeKey, true);
          mergedEvents.push(normalizedEvent);
        }
      }

      setGoogleEventsCache(mergedEvents);
      return { events: mergedEvents };
    } catch (error) {
      setGoogleEventsError(error);
      return { events: [] };
    } finally {
      setGoogleEventsLoading(false);
    }
  }, [apolloClient, businessStaffs, currentMonthKey, providerUser?.staffId, loggedInStaffEmail, isMySchedule]);

  // Refetch bookings once on mount.
  useEffect(() => {
    refetchBookings();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      refetchBookings({ fetchPolicy: 'network-only' });
      if (googleEventsEnabled) {
        refetchGoogleEvents({ currentMonth: currentMonthKey });
      }
    }, pollMs);

    return () => window.clearInterval(timer);
  }, [pollMs, refetchBookings, googleEventsEnabled, refetchGoogleEvents, currentMonthKey]);

  useEffect(() => {
    const handleWindowRefresh = () => {
      refetchBookings({ fetchPolicy: 'network-only' });
      if (googleEventsEnabled) {
        refetchGoogleEvents({ currentMonth: currentMonthKey });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleWindowRefresh();
      }
    };

    window.addEventListener('focus', handleWindowRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchBookings, googleEventsEnabled, refetchGoogleEvents, currentMonthKey]);

  // Keep Google events normalized for team filtering.
  const googleEventsArray = useMemo(() => {
    return googleEventsCache || [];
  }, [googleEventsCache]);

  // Ensure base bookings are an array.
  const baseBookings = useMemo(() => {
    return Array.isArray(bookingsFromStore) ? bookingsFromStore : [];
  }, [bookingsFromStore]);

  // Custom merge function to update bookingDatas.
  const updateBookingDatas = useCallback(() => {
    const mergedBookingData = [...baseBookings, ...googleEventsArray, ...holidayDatas];
    setAllBookingDatas(mergedBookingData);
    setBookingDatas(mergedBookingData);
  }, [baseBookings, googleEventsArray, holidayDatas]);

  // Update bookingDatas when base bookings or Google events change.
  useEffect(() => {
    updateBookingDatas();
  }, [updateBookingDatas]);

  // Ensure Google data refreshes when the toggle is turned on or month changes.
  useEffect(() => {
    if (!googleEventsEnabled) {
      setGoogleEventsCache([]);
      return;
    }
    refetchGoogleEvents({ currentMonth: currentMonthKey });
  }, [googleEventsEnabled, currentMonthKey, refetchGoogleEvents]);

  // Expose a refresh function to force an update after deletion.
  const refreshBookings = useCallback(async () => {
    // Force network fetch to get the latest data.
    await refetchBookings({ fetchPolicy: 'network-only' });
    if (extraYear) await refetchExtraBookings();
    updateBookingDatas();
  }, [refetchBookings, refetchExtraBookings, extraYear, updateBookingDatas]);

  const getGridStyle = (dateInput, verify, ind) => {
    const isSelected = selectedDate && isSameDay(dateInput, selectedDate);
    return {
      transition: 'transform 0.3s ease-in-out',
      overflow: 'hidden',
      userSelect: 'none',
      padding: '10px',
      width: '140px',
      minWidth: isSelected ? '140px' : '',
      height: '140px',
      border: '1px solid var(--text-color-level3,rgba(26,26,26,0.1))',
      borderTop: ind < 7 ? 'none' : undefined,
      borderLeft: ind < 7 ? 'none' : undefined,
      background: verify ? theme.palette.action.hover : theme.palette.background.paper,
      zIndex: isSelected ? 2 : 1,
    };
  };

  const toggleDate = (newDate) => {
    setSelectedDate(newDate);
    setCurrentMonth(newDate);
  };

  const contextValue = {
    ...calendarPreferences,
    displayTimezone,
    allBookingDatas,
    bookingDatas,
    setHolidayDatas,
    updateBookingDatas,
    setBookingDatas,
    refreshBookings,
    currentMonth,
    setCurrentMonth,
    selectedDate,
    toggleDate,
    providerUser,
    setSelectedDate,
    getGridStyle,
    myBookingTypes,
    selectedBusiness,
    businessStaffs,
    googleEventsEnabled,
    setGoogleEventsEnabled,
    isCalendarLoading: bookingsLoading || (googleEventsEnabled && googleEventsLoading),
    refetchBookings,
    reloadBusinesses,
    refetchGoogleEvents,
    registerBusiness,
  };

  if (googleEventsError) {
    console.error('Google calendar events query error:', googleEventsError);
  }

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = () => useContext(CalendarContext);
